<script>
import draggable from "vuedraggable";
import MediaPickerModal from "@/components/media/MediaPickerModal.vue";
import MediaPreview from "@/components/media/MediaPreview.vue";
import siteIdentityApi from "@/helpers/site-identity/site-identity-api.js";

export default {
  name: "SiteIdentitySectionEditor",
  components: {
    draggable,
    MediaPickerModal,
    MediaPreview,
  },
  props: {
    sectionKey: {
      type: String,
      required: true,
    },
    sectionConfig: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      localItems: [],
      selectedMediaSingle: null,
      savedSnapshot: "",
      saving: false,
    };
  },
  computed: {
    isMultiple() {
      return !!this.sectionConfig.multiple;
    },
    maxItems() {
      return this.sectionConfig.max_items || 1;
    },
    itemCount() {
      return this.isMultiple
        ? this.localItems.length
        : this.selectedMediaSingle
          ? 1
          : 0;
    },
    counterLabel() {
      return `${this.itemCount}/${this.maxItems}`;
    },
    isAtMaxCapacity() {
      return this.itemCount >= this.maxItems;
    },
    canAddMore() {
      return this.itemCount < this.maxItems;
    },
    pickerModalId() {
      return `siteIdentityPicker_${this.sectionKey}`;
    },
    isDirty() {
      return this.buildSnapshot() !== this.savedSnapshot;
    },
    pickerSelected() {
      if (this.isMultiple) {
        return this.localItems.map((item) => item.media);
      }
      return this.selectedMediaSingle ? [this.selectedMediaSingle] : [];
    },
  },
  watch: {
    sectionConfig: {
      immediate: true,
      deep: true,
      handler(config) {
        this.initFromConfig(config);
      },
    },
  },
  methods: {
    initFromConfig(config) {
      if (!config?.items) {
        return;
      }

      if (this.isMultiple) {
        this.localItems = config.items
          .filter((item) => item.media)
          .map((item) => ({
            media_id: item.media_id,
            media: item.media,
            is_active: item.is_active !== false,
          }));
      } else {
        const first = config.items.find((item) => item.media);
        this.selectedMediaSingle = first?.media || null;
      }

      this.savedSnapshot = this.buildSnapshot();
    },
    buildSnapshot() {
      if (this.isMultiple) {
        return JSON.stringify(
          this.localItems.map((item) => ({
            media_id: item.media_id,
            is_active: item.is_active,
          }))
        );
      }

      return JSON.stringify({
        media_id: this.selectedMediaSingle?.id || null,
      });
    },
    buildSyncItems() {
      if (this.isMultiple) {
        return this.localItems.map((item) => ({
          media_id: item.media_id,
          is_active: item.is_active,
        }));
      }

      return this.selectedMediaSingle
        ? [{ media_id: this.selectedMediaSingle.id, is_active: true }]
        : [];
    },
    openPicker() {
      this.$bvModal.show(this.pickerModalId);
    },
    onPickerConfirm(payload) {
      if (this.isMultiple) {
        const existing = new Map(
          this.localItems.map((item) => [item.media_id, item])
        );
        const next = (Array.isArray(payload) ? payload : [])
          .slice(0, this.maxItems)
          .map((media) => {
            const previous = existing.get(media.id);
            return {
              media_id: media.id,
              media,
              is_active: previous ? previous.is_active : true,
            };
          });

        this.localItems = next;
        return;
      }

      this.selectedMediaSingle = payload || null;
    },
    removeLocalItem(mediaId) {
      this.localItems = this.localItems.filter(
        (item) => item.media_id !== mediaId
      );
    },
    setItemActive(mediaId, isActive) {
      const index = this.localItems.findIndex(
        (entry) => entry.media_id === mediaId
      );
      if (index === -1) {
        return;
      }

      this.$set(this.localItems[index], "is_active", isActive);
    },
    clearAllMedia() {
      if (this.isMultiple) {
        this.localItems = [];
        return;
      }

      this.selectedMediaSingle = null;
    },
    applyUpdatedSection(updated) {
      if (!updated) {
        return;
      }

      this.initFromConfig(updated);
    },
    async saveSection() {
      if (!this.isDirty) {
        return;
      }

      this.saving = true;

      try {
        const response = await siteIdentityApi.syncSection(
          this.sectionKey,
          this.buildSyncItems()
        );
        const sections = response?.data?.data || {};
        this.applyUpdatedSection(sections[this.sectionKey]);

        siteIdentityApi.clearSiteIdentityCache();
        this.$emit("saved");
        this.$bvToast.toast("Configuración guardada correctamente", {
          title: "Identidad del sitio",
          variant: "success",
          solid: true,
        });
      } catch (error) {
        const message =
          error?.response?.data?.errors?.[0]?.title ||
          error?.response?.data?.message ||
          "Error al guardar la sección";
        this.$bvToast.toast(message, {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div class="site-identity-section">
    <div class="site-identity-section__header">
      <div>
        <p v-if="sectionConfig.description" class="site-identity-section__description">
          {{ sectionConfig.description }}
        </p>
      </div>
      <div class="site-identity-section__meta">
        <span
          v-if="isDirty"
          class="site-identity-unsaved"
        >
          Cambios sin guardar
        </span>
        <span
          class="site-identity-counter"
          :class="{ 'site-identity-counter--full': isAtMaxCapacity }"
        >
          {{ counterLabel }}
        </span>
      </div>
    </div>

    <!-- Multiple: unified draggable list -->
    <div v-if="isMultiple" class="site-identity-list">
      <template v-if="localItems.length">
        <draggable
          v-model="localItems"
          class="site-identity-draggable"
          handle=".drag-handle"
          :disabled="!sectionConfig.reorderable"
        >
          <div
            v-for="(item, index) in localItems"
            :key="item.media_id"
            class="site-identity-item"
            :class="{ 'site-identity-item--inactive': !item.is_active }"
          >
            <span
              v-if="sectionConfig.reorderable"
              class="drag-handle"
              title="Arrastrar para reordenar"
            >
              <i class="ri-drag-move-2-line"></i>
            </span>

            <div class="site-identity-item__index">{{ index + 1 }}</div>

            <MediaPreview :item="item.media" size="3.5rem" class="site-identity-item__preview" />

            <div class="site-identity-item__info">
              <div class="site-identity-item__name">{{ item.media.name }}</div>
              <div class="site-identity-item__alt">
                {{ item.media.alt || "Sin texto alternativo" }}
              </div>
            </div>

            <div class="site-identity-item__actions">
              <b-form-checkbox
                :checked="item.is_active"
                switch
                class="site-identity-item__toggle"
                @change="setItemActive(item.media_id, $event)"
              >
                <span class="site-identity-item__toggle-label">Activo</span>
              </b-form-checkbox>

              <button
                type="button"
                class="site-identity-item__remove"
                title="Quitar logo"
                @click="removeLocalItem(item.media_id)"
              >
                <i class="ri-delete-bin-line"></i>
              </button>
            </div>
          </div>
        </draggable>

        <button
          v-if="canAddMore"
          type="button"
          class="site-identity-add-card"
          @click="openPicker"
        >
          <i class="ri-image-add-line site-identity-add-card__icon"></i>
          <span class="site-identity-add-card__title">Añadir logo</span>
          <span class="site-identity-add-card__hint">
            {{ maxItems - itemCount }} espacio(s) disponible(s)
          </span>
        </button>
      </template>

      <div v-else class="site-identity-empty">
        <i class="ri-image-line site-identity-empty__icon"></i>
        <p>No hay logos configurados. Añade hasta {{ maxItems }} imágenes.</p>
        <b-button size="sm" variant="outline-primary" @click="openPicker">
          <i class="ri-add-line mr-1"></i>
          Seleccionar desde biblioteca
        </b-button>
      </div>
    </div>

    <!-- Single: large preview card -->
    <div v-else class="site-identity-single">
      <div
        v-if="selectedMediaSingle"
        class="site-identity-single__preview"
      >
        <img
          :src="selectedMediaSingle.url"
          :alt="selectedMediaSingle.alt || selectedMediaSingle.name"
          class="site-identity-single__image"
        />
        <div class="site-identity-single__meta">
          <div class="site-identity-single__name">{{ selectedMediaSingle.name }}</div>
          <div class="site-identity-single__alt text-muted">
            {{ selectedMediaSingle.alt || "Sin texto alternativo" }}
          </div>
        </div>
        <div class="site-identity-single__actions">
          <b-button size="sm" variant="outline-primary" @click="openPicker">
            <i class="ri-refresh-line mr-1"></i>
            Cambiar
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="clearAllMedia">
            <i class="ri-delete-bin-line mr-1"></i>
            Quitar
          </b-button>
        </div>
      </div>

      <button
        v-else
        type="button"
        class="site-identity-add-card site-identity-add-card--large"
        @click="openPicker"
      >
        <i class="ri-image-add-line site-identity-add-card__icon"></i>
        <span class="site-identity-add-card__title">Seleccionar imagen</span>
        <span class="site-identity-add-card__hint">
          Elige un archivo de la biblioteca de medios
        </span>
      </button>
    </div>

    <MediaPickerModal
      :modal-id="pickerModalId"
      :single="!isMultiple"
      :selected="pickerSelected"
      :only-images="sectionConfig.only_images !== false"
      :ok-button-label="isMultiple ? 'Confirmar selección' : 'Seleccionar'"
      @confirm="onPickerConfirm"
    />

    <div class="site-identity-section__footer">
      <div class="site-identity-section__footer-hint">
        <i class="ri-information-line mr-1"></i>
        <template v-if="isMultiple">
          Orden, selección y visibilidad se guardan al pulsar «Guardar cambios».
        </template>
        <template v-else>
          Los cambios se aplican al pulsar «Guardar cambios».
        </template>
      </div>
      <b-button
        variant="primary"
        :disabled="saving || !isDirty"
        @click="saveSection"
      >
        <b-spinner v-if="saving" small class="mr-1" />
        <i v-else class="ri-save-line mr-1"></i>
        Guardar cambios
      </b-button>
    </div>
  </div>
</template>

<style scoped>
.site-identity-section {
  max-width: 820px;
}

.site-identity-section__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.site-identity-section__description {
  margin: 0;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
}

.site-identity-section__meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.site-identity-unsaved {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  background: #fff3e0;
  color: #e65100;
  white-space: nowrap;
}

.site-identity-counter {
  display: inline-flex;
  align-items: center;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  background: #e8f5e9;
  color: #2e7d32;
  white-space: nowrap;
}

.site-identity-counter--full {
  background: #fff3e0;
  color: #e65100;
}

.site-identity-draggable {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.site-identity-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  background: #fff;
  transition: box-shadow 0.2s ease, opacity 0.2s ease;
}

.site-identity-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.site-identity-item--inactive {
  opacity: 0.55;
  background: #f8f9fa;
}

.drag-handle {
  cursor: grab;
  color: #adb5bd;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.drag-handle:active {
  cursor: grabbing;
}

.site-identity-item__index {
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #f1f3f5;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.site-identity-item__preview {
  flex-shrink: 0;
}

.site-identity-item__info {
  flex: 1;
  min-width: 0;
}

.site-identity-item__name {
  font-weight: 600;
  font-size: 0.875rem;
  color: #343a40;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.site-identity-item__alt {
  font-size: 0.78rem;
  color: #868e96;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 0.15rem;
}

.site-identity-item__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.site-identity-item__toggle {
  margin: 0;
}

.site-identity-item__toggle-label {
  font-size: 0.8rem;
  color: #6c757d;
}

.site-identity-item__remove {
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #adb5bd;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.site-identity-item__remove:hover {
  background: #fff5f5;
  color: #e53935;
}

.site-identity-add-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  margin-top: 0.65rem;
  padding: 1.25rem;
  border: 2px dashed #d1d9e0;
  border-radius: 10px;
  background: #fafbfc;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.site-identity-add-card:hover {
  border-color: #6ea637;
  background: #f5f9f0;
}

.site-identity-add-card--large {
  min-height: 200px;
  margin-top: 0;
}

.site-identity-add-card__icon {
  font-size: 2rem;
  color: #6ea637;
  margin-bottom: 0.5rem;
}

.site-identity-add-card__title {
  font-weight: 600;
  font-size: 0.9rem;
  color: #495057;
}

.site-identity-add-card__hint {
  font-size: 0.78rem;
  color: #868e96;
  margin-top: 0.25rem;
}

.site-identity-empty {
  text-align: center;
  padding: 2.5rem 1rem;
  border: 1px dashed #dee2e6;
  border-radius: 10px;
  background: #fafbfc;
  color: #6c757d;
}

.site-identity-empty__icon {
  font-size: 2.5rem;
  color: #ced4da;
  margin-bottom: 0.75rem;
  display: block;
}

.site-identity-empty p {
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.site-identity-single__preview {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
}

.site-identity-single__image {
  width: 100%;
  max-height: 220px;
  object-fit: contain;
  background: #f8f9fa;
  padding: 1rem;
  display: block;
}

.site-identity-single__meta {
  padding: 0.85rem 1rem 0;
}

.site-identity-single__name {
  font-weight: 600;
  font-size: 0.9rem;
}

.site-identity-single__alt {
  font-size: 0.8rem;
  margin-top: 0.15rem;
}

.site-identity-single__actions {
  display: flex;
  gap: 0.5rem;
  padding: 0.85rem 1rem 1rem;
}

.site-identity-section__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid #e9ecef;
}

.site-identity-section__footer-hint {
  font-size: 0.8rem;
  color: #868e96;
  flex: 1;
}
</style>
